import React, { RefObject } from "react";
import { apiGet, apiPost, bytesToStr, getAgentUrl, SpaceService } from "./Utilities";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FileItem } from "./SharedFiles"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TdFile, TdLoginInfo, TdSpace } from "../TdTypes";
import { compFn, mkCompFn, SortSelection } from "./Sorting";
import { WithTranslation, withTranslation } from "react-i18next";

class FileSelector extends React.Component<
    WithTranslation & {
        callback: (file : TdFile) => void
    }, 
    {
        spaces: TdSpace[],
        loaded: boolean,
        error: string,
        showModal: boolean,
        fileSelectorMode: 'file' | 'folder',
        fileSelectorCallback: (file : TdFileSelectorResult) => void,
        fileSelectorShown: boolean,
    }
> {
  private inputFile : RefObject<HTMLInputElement>;

  constructor(props : any) {
    super(props);
    this.state = { 
      spaces: [],
      loaded: false,
      error: '',
      showModal: false,
      fileSelectorMode: 'file',
      fileSelectorCallback: () => {},
      fileSelectorShown: false
    };

    this.inputFile = React.createRef();
  }

  componentDidMount() {
    if (!this.inputFile.current)
      throw new Error(this.props.t('Unable to access file selection element.'));

    // setup the callback for when the user selects a local file to upload
    this.inputFile.current.onchange = (event : any) => {
      const f : File = event.target.files[0];

      this.setState({
        showModal: false,
        fileSelectorMode: 'folder', 
        fileSelectorShown: true,
        fileSelectorCallback: (tdFolder) => {  // FIXME this can also be a space
          this.setState({
            fileSelectorShown: false
          });
          this.uploadFile(f, tdFolder.spaceId, tdFolder.path);
        }
      });
    };
  }

  uploadFile = (file : File, spaceId : number, spacePath : string) => {
    this.setState({ error: '' });

    fetch(getAgentUrl() + '/files/' + spaceId + spacePath + encodeURIComponent(file.name), {
      method: "PUT",
      headers: {
        'Content-Type': (file.type === '' ? 'application/octet-stream' : file.type),
        'Content-Length': '' + file.size
      }, 
      body: file
    }).then(res => res.json()).then((response) => {
      this.props.callback(response.file as TdFile);
    }).catch((error) => {
      console.error(`Failed to upload file`);
      this.setState({ error: `Failed to upload file: '${error.message}'` });
    });
  };

  selectFileFromSpace = () => {
    this.setState({
      showModal: false,
      fileSelectorMode: 'file', 
      fileSelectorShown: true,
      fileSelectorCallback: (tdFile) => {
        this.setState({ fileSelectorShown: false });
        this.props.callback(tdFile as TdFile);
      }
    });
  };

  selectFileFromPC = () => {
    if (this.inputFile.current)
      this.inputFile.current.click();
  };

  openModal = () => this.setState({ showModal: true });
  closeModal = () => this.setState({ showModal: false });

  render() {
    const t = this.props.t;

    return (
      <>
        <Button variant="primary" onClick={this.openModal}>
          {t('Add file')} <FontAwesomeIcon icon="cloud-arrow-up" className="btn-icon" />
        </Button>

        { this.state.error && (<div className="alert alert-danger">{this.state.error}</div>)}

        <Modal show={this.state.showModal} onHide={this.closeModal} size="sm">
          <Modal.Header closeButton>
            <Modal.Title> {t('Add file')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <Button variant="primary" onClick={this.selectFileFromSpace}>
              {t('Select from Space')} <FontAwesomeIcon icon="cloud-arrow-up" className="btn-icon" />
            </Button> 
            <hr/>
            <Button variant="primary" onClick={this.selectFileFromPC}>
              {t('Select from PC')} <FontAwesomeIcon icon="upload" className="btn-icon" />
            </Button> 
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeModal}>{t('Cancel')}</Button>
          </Modal.Footer>
        </Modal>

        <TdFileSelector 
          shown={this.state.fileSelectorShown}
          mode={this.state.fileSelectorMode} 
          callback={this.state.fileSelectorCallback}
          onCancel={() => this.setState({fileSelectorShown: false})}
          >
        </TdFileSelector>

        <input type='file' id='file' ref={this.inputFile} style={{display: 'none'}}/>
      </>
    );
  }
}

export default withTranslation()(FileSelector);

// creates a mouse-event-handler function which stops event propagation before running the given callback
function sp(callback : ()=>void) : React.MouseEventHandler {
  return (event : React.MouseEvent) => {
    event.stopPropagation();
    callback();
  };
}

export type TdFileSelectorResult = TdFile | {
  spaceId : number,
  path: string
};
const TdFileSelector = withTranslation()(class extends React.Component<WithTranslation & {
    shown: boolean,
    mode: 'file' | 'folder',
    callback: (TdFileOrFolderOrSpace : TdFileSelectorResult) => void,
    onCancel: () => void
  }>{
    state = { 
      loaded: false,
      error: '',
      spaces: [] as TdSpace[],
      spaceId: 0,
      spaceName: "",
      pathSegs: [] as string[],
      files: [] as TdFile[],
      enableGuiActions: false
    };

    private sortOptions = {
      space: [
        [this.props.t('Sort by name'), mkCompFn(s => s.name, (n1, n2) => n1.localeCompare(n2))],
        [this.props.t('Sort by creator'), mkCompFn(s => s.creator, (n1, n2) => n1.localeCompare(n2))],
      ] as [string, compFn<TdSpace>][],
      file: [
        [this.props.t('Sort by name'), mkCompFn(f => f.name, (n1, n2) => n1.localeCompare(n2))],
        [this.props.t('Sort by creator'), mkCompFn(f => f.creator, (n1, n2) => n1.localeCompare(n2))],
        [this.props.t('Sort by size'), mkCompFn(f => f.size as number, (n1, n2) => n1-n2)],
      ] as [string, compFn<TdFile>][],
    };

    componentDidMount() {
      this.load(0, []);

      apiGet<TdLoginInfo>('getLoginInformation').then(info => {
        this.setState({ enableGuiActions: !info.isDaemon });
      });
    }

    getSelectedSpaceName = () => {
      if (this.state.spaceId === 0)
        return Promise.resolve("");
        
      return SpaceService.getSpace(this.state.spaceId).then(space => space.name);
    };

    load = (spaceId : number, pathSegments : string[]) : Promise<any> => {
      this.setState({ loaded: false });

      if (spaceId === 0) {
        // load spaces
        return SpaceService.getSpaces().then(data => {
          this.setState({spaces: data.filter(s => s.status === "active"), loaded: true, error: '', spaceId: 0, spaceName: "", pathSegs: []});
          this.updateListSort(0, false);
        })
        .catch(error => {
          console.error(`Failed to fetch spaces from TeamDrive: ${error}`);
          this.setState({ error: error.message });
        });
      } else {
        // load files
        return apiGet('findFiles', {
          "spaceIds": spaceId,
          "path": '/' + pathSegments.join('/'),
          "sortBy": "isDir,name",
          "limit": "200",
          "filters": "!trashed"
        })
        .then(data => {
          this.setState({files: data.files, loaded: true, spaceId: spaceId, pathSegs: pathSegments, error: '' });

          return SpaceService.getSpace(spaceId);
        })
        .then(space => {
          this.setState({spaceName: space.name});
          this.updateListSort(0, false);
        })
        .catch(error => {
          console.error(`Failed to fetch files from TeamDrive: ${error}`);
          this.setState({ error: error.message });
        });
      }
    };

    navUp = () => {
      if (this.state.pathSegs.length > 0) {
        this.load(this.state.spaceId, this.state.pathSegs.slice(0,-1));
      } else {
        this.load(0, []);
      }
    };

    selectFolder = (tdFolder : any) => {
      this.props.callback({
        spaceId: tdFolder.spaceId,
        path: tdFolder.path + tdFolder.name + '/'
      });
    };

    selectSpaceAsFolder = (tdSpace : any) => {
      this.props.callback({
        spaceId: tdSpace.id,
        path: '/'
      });
    };

    updateListSort = (opt : number, reverse : boolean) => {
      if (this.state.spaceId) {
        // inside a space, sort files
        this.setState({
          files: this.state.files.sort((a,b) => this.sortOptions.file[opt][1](a,b,reverse))
        });
      } else {
        // viewing spaces, sort spaces
        this.setState({
          spaces: this.state.spaces.sort((a,b) => this.sortOptions.space[opt][1](a,b,reverse))
        });
      }
    }; 

    showSpaceInfo = (space : TdSpace) => {
      if (this.state.enableGuiActions) {
        apiPost("contextMenuAction", {
          id: 4,
          path: space.spaceRoot
        }).catch(err => this.setState({error: err.message}));
      } else {
        window.open(getAgentUrl() + '/space/' + space.id + '/info');
      }
    };

    showFileInfo = (file : TdFile) => {
      if (this.state.enableGuiActions) {
        SpaceService.getSpace(file.spaceId).then(space => {
          return apiPost("contextMenuAction", {
            id: 3,
            path: space.spaceRoot + file.path + file.name
          });
        }).catch(err => this.setState({error: err.message}));
      } else {
        window.open(getAgentUrl() + '/fileref/' + file.globalId);
      }
    };

    render() {
      const t = this.props.t;

      return (
        <Modal show={this.props.shown} onHide={this.props.onCancel}>
          <Modal.Header closeButton>
            <Modal.Title>{t('Select')} {this.props.mode === 'file' ? t('File') : t('Folder')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="td-fileselector">
              { this.state.error && (<div className="alert alert-danger">{this.state.error}</div>)}
              <div className="td-fileselector-header">
                <div className="td-fileselector-title">{ this.state.spaceId === 0 ? "Spaces:" : this.state.spaceName }</div>
                { this.state.spaceId !== 0 && <div className="td-fileselector-subtitle">{"/" + this.state.pathSegs.join("/")}</div> }
                { this.state.spaceId !== 0 && 
                  <div className="td-fileselector-navbtns">
                    <SortSelection 
                      options={this.sortOptions.file.map(o => o[0])} 
                      onChange={this.updateListSort}>
                    </SortSelection>
                    <button onClick={this.navUp} className="btn btn-primary btn-sm">
                      <FontAwesomeIcon icon="turn-up" />
                    </button>
                    <button onClick={() => this.load(0, [])} className="btn btn-primary btn-sm">
                      <FontAwesomeIcon icon="house" />
                    </button>
                  </div>
                }
                { this.state.spaceId === 0 && 
                  <div className="td-fileselector-navbtns">
                    <SortSelection 
                      options={this.sortOptions.space.map(o => o[0])} 
                      onChange={this.updateListSort}>
                    </SortSelection>
                  </div>
                }
              </div>

              { !this.state.loaded 
                ? (<b>{t('Loading...')}</b>)
                : (this.state.spaceId === 0 
                  ? (this.state.spaces.length === 0 
                    ? ( <ul><li>{t('No active Spaces found')}</li></ul> )
                    : (
                      // spaces
                      this.state.spaces.map((space, indx) => 
                        <FileItem
                          key={indx}
                          name={space.name}
                          icon="users"
                          subtext={t('Created by:') + ' ' + space.creator}
                          onClick={() => this.load(space.id, [])}
                        >
                          { this.props.mode === 'folder' && (<div className="btn-section">
                            <button className="btn btn-primary btn-sm" onClick={sp(() => {this.selectSpaceAsFolder(space);})}>{t('select')}</button>
                          </div>) }
                          <div className="btn-section">
                            <button className="btn single-char-btn" onClick={sp(() => this.showSpaceInfo(space))}><FontAwesomeIcon icon='info' /></button>
                          </div>
                        </FileItem>
                      )
                    )
                  )
                  : (this.state.files.length === 0
                    ? (<ul><li>{t('Folder is empty')}</li></ul>)
                    : (
                      <>
                        {/* folders  */}
                        { this.state.files.filter(f => f.isDir).map((f, indx) => 
                          <FileItem
                            key={indx}
                            name={f.name}
                            icon="folder-open"
                            subtext={t('Created by:') + ' ' + f.creator}
                            onClick={() => this.load(this.state.spaceId, this.state.pathSegs.concat([f.name]))}
                            >
                            { this.props.mode === 'folder' && (<div className="btn-section">
                              <button className="btn btn-primary btn-sm" onClick={sp(() => {this.selectFolder(f);})}>{t('select')}</button>
                            </div>) }
                            <div className="btn-section">
                              <button className="btn single-char-btn" onClick={sp(()=>this.showFileInfo(f))}><FontAwesomeIcon icon='info' /></button>
                            </div>
                          </FileItem>
                        ) }
                        {/* files  */}
                        { this.props.mode === 'file' && this.state.files.filter(f => !f.isDir).map((f, indx) => 
                          <FileItem key={indx} name={f.name} icon="file" subtext={t('Created by:') + ' ' + f.creator + ', ' + bytesToStr(f.size)}>
                            <div className="btn-section">
                              <button className="btn btn-primary btn-sm" onClick={() => {this.props.callback(f);}}>{t('select')}</button>
                            </div>
                            <div className="btn-section">
                              <button className="btn single-char-btn" onClick={() => this.showFileInfo(f)}><FontAwesomeIcon icon='info' /></button>
                            </div>
                          </FileItem>
                        ) }
                      </>
                    )
                  )
                )
              }
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.props.onCancel}>{t('Cancel')}</Button>
          </Modal.Footer>
        </Modal>
      );
    }
  }
)
